// 腾讯云 COS 直传工具
// 目的：前端拿到临时密钥后直接上传文件到 COS，绕开业务服务器 body 限制（支持 1GB+ 大文件）
// 依赖：cos-js-sdk-v5；接口 getUploadPolicy 返回临时密钥 + 桶/路径信息
import COS from 'cos-js-sdk-v5'
import { getUploadPolicy } from '@/api/api'

// 单例 COS 实例（避免每次上传都重新初始化）
let cosInstance = null

// 获取腾讯云 COS 临时密钥（STS）
// 返回：{ bucket, region, tmpSecretId, tmpSecretKey, sessionToken, startTime, expiredTime, baseHttp, filePath }
export function fetchCosPolicy () {
  return getUploadPolicy().then(res => {
    if (res && res.data) return res.data
    throw new Error('获取腾讯云上传配置失败')
  })
}

// 初始化 COS 实例（单例）
// getAuthorization 回调内会自动请求最新临时密钥（密钥过期 SDK 会再次调用此回调）
export function getCosInstance () {
  if (cosInstance) return cosInstance
  cosInstance = new COS({
    SimpleUploadMethod: 'putObject',
    getAuthorization: async (options, callback) => {
      try {
        const data = await fetchCosPolicy()
        callback({
          TmpSecretId: data.tmpSecretId,
          TmpSecretKey: data.tmpSecretKey,
          SecurityToken: data.sessionToken,
          StartTime: data.startTime,
          ExpiredTime: data.expiredTime
        })
      } catch (error) {
        // 第二个参数传 error，SDK 会终止上传并抛出
        callback(null, error)
      }
    }
  })
  return cosInstance
}

// 从文件名生成 COS 对象 Key：保留原名 + 防重命名冲突的时间戳前缀
// 参考实现直接用原名（无时间戳），同名会覆盖。这里加时间戳前缀，避免并发/重名覆盖
function buildObjectKey (filePath, fileName) {
  const dotIdx = fileName.lastIndexOf('.')
  const base = dotIdx > 0 ? fileName.slice(0, dotIdx) : fileName
  const ext = dotIdx > 0 ? fileName.slice(dotIdx + 1) : ''
  const stamp = Date.now()
  const rand = Math.random().toString(36).slice(2, 8)
  // 最终 Key：filePath/原名_时间戳_随机.扩展名
  return ext ? `${filePath}/${base}_${stamp}_${rand}.${ext}` : `${filePath}/${base}_${stamp}_${rand}`
}

/**
 * 直传文件到腾讯云 COS
 * @param {File} file 浏览器 File 对象
 * @param {(percent:number)=>void} [onProgress] 上传进度回调，percent 取值 0~1
 * @returns {Promise<{url:string, key:string}>} url 为可访问 URL，key 为 COS 对象 Key
 */
export function uploadToCos (file, onProgress) {
  return new Promise((resolve, reject) => {
    fetchCosPolicy().then(policy => {
      const cos = getCosInstance()
      const objectKey = buildObjectKey(policy.filePath, file.name)
      cos.uploadFile({
        Bucket: policy.bucket,
        Region: policy.region,
        Key: objectKey,
        Body: file,
        // 分片大小 5MB（大文件走分片上传，可断点续传）
        SliceSize: 1024 * 1024 * 5,
        onProgress: (progressData) => {
          if (typeof onProgress === 'function' && typeof progressData.percent === 'number') {
            onProgress(progressData.percent)
          }
        }
      }).then(() => {
        // 可访问 URL = baseHttp + 对象 Key（baseHttp 已含桶的自定义域名）
        const url = `${policy.baseHttp}${objectKey}`
        resolve({ url, key: objectKey })
      }).catch(err => {
        reject(new Error(`腾讯云上传失败：${(err && err.message) || '未知错误'}`))
      })
    }).catch(err => reject(err))
  })
}
